import React, { useEffect, useState } from 'react';
import { Col, Spacer, View } from '@amzn/stencil-react-components/layout';
import { H2 } from '@amzn/stencil-react-components/text';
import { FormattedMessage, useIntl } from 'react-intl';
import CampaignDetailsForm from '../components/journey/CampaignDetailsForm';
import { Button } from '@amzn/stencil-react-components/button';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { HOME_PAGE } from '../constants/routesConstant';
import JourneyDetailsForm from '../components/journey/JourneyDetailsForm';
import {
  buildCreateMultiCampaignPayload,
  validateAllCampaignsInput,
} from '../util/journey/journeyUtils';
import { useDispatch, useSelector } from 'react-redux';
import {
  addNewCampaign,
  createMultiCampaign,
  resetJourneyDetails,
  setFormView,
  submitMultiCampaignForm,
} from '../actions/journeyDetailsAction';
import {
  journeyDetailsCampaignsSelector,
  journeyDetailsSelector,
  multiCampaignDescriptionSelector,
  multiCampaignFormViewSelector,
  multiCampaignNameSelector,
  multiCampaignResponseSelector,
} from '../selectors/selectors';
import { useUserInfo } from '../hooks/useUserInfo';
import { getAllMessageTemplates, getAllSegments } from '../actions/nudgeActions';
import ButtonWithLoader from '../components/atoms/ButtonWithLoader';
import { ResponseStatus } from '../enums/responseStatus';
import { MessageBanner } from '@amzn/stencil-react-components/message-banner';
import { FormErrorMessage, Message, MULTI_CAMPAIGN_FORM_INFO } from '../enums/schedulingConstant';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';

import { IconInformationSmall } from '@amzn/stencil-react-icons';

const IconWithTooltip = withTooltip()(IconInformationSmall);
const JourneyContainer = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const journeyName = useSelector(multiCampaignNameSelector);
  const journeyDescription = useSelector(multiCampaignDescriptionSelector);
  const campaignList = useSelector(journeyDetailsCampaignsSelector);
  const readonly = useSelector(multiCampaignFormViewSelector);
  const formData = useSelector(journeyDetailsSelector);
  const [loading, setLoading] = useState(false);
  const { status } = useSelector(multiCampaignResponseSelector);
  const [isValid, setIsValid] = useState(false);

  const userInfo = useUserInfo();
  const journeyDetailsFormData = {
    journeyName,
    journeyDescription,
  };

  useEffect(() => {
    (async () => {
      await dispatch(getAllSegments(userInfo.selectedAccessGroupName, userInfo.idToken));
      await dispatch(getAllMessageTemplates(userInfo.selectedAccessGroupName, userInfo.idToken));
    })();

    return () => {
      dispatch(resetJourneyDetails());
    };
  }, []);

  const addSectionClickHandler = () => {
    dispatch(addNewCampaign());
  };

  useEffect(
    () => {
      if (status === ResponseStatus.SUCCESS) {
        dispatch(setFormView(true));
      } else {
        dispatch(setFormView(false));
      }
      setLoading(false);
    },
    [status],
  );

  const onCreateJourneyClick = async () => {
    dispatch(submitMultiCampaignForm());
    const isFormValid = validateAllCampaignsInput(campaignList);
    setIsValid(isFormValid);
    if (isFormValid) {
      setLoading(true);
      const payload = buildCreateMultiCampaignPayload(formData, userInfo);
      await dispatch(createMultiCampaign(payload, userInfo.idToken));
    }
  };

  return (
    <>
      <Col width={'98%'} flex="1" gridGap={'S300'} data-testid="create-segment">
        <View display="flex" flexDirection="row">
          <H2>
            <FormattedMessage id="app.journey.createAjourney" defaultMessage="Create a Journey" />
          </H2>

          <IconWithTooltip
            title="Form validation"
            tooltipText={MULTI_CAMPAIGN_FORM_INFO}
            color="primary70"
          />
        </View>

        <Col display="flex" flexDirection="row">
          <JourneyDetailsForm data={journeyDetailsFormData} readonly={readonly} />
        </Col>

        <Col>
          {campaignList?.map((campaign, index) => (
            <>
              <CampaignDetailsForm formData={campaign} readonly={readonly} />
              <Spacer key={`spacer${index}`} height={15} />
            </>
          ))}
        </Col>

        <Col alignItems="start" display="flex">
          <Button
            data-testid="add-another-btn"
            disabled={readonly}
            fontSize="T500"
            variant="tertiary"
            onClick={addSectionClickHandler}
            css={{paddingLeft: 0}}
          >
            <FormattedMessage
              id="app.groupCampaign.addAnotherSection"
              defaultMessage="Add another section"
            />
          </Button>
        </Col>
        <Col>
          {!loading &&
            formData?.isFormSubmitted && (
              <MessageBanner
                data-testid="message-banner"
                type={status ? status : ResponseStatus.ERROR}
              >
                {status
                  ? Message[status]
                  : !isValid
                    ? FormErrorMessage
                    : Message[ResponseStatus.ERROR]}
              </MessageBanner>
            )}
        </Col>
        <Col
          alignItems="center"
          justifyContent="start"
          display="flex"
          flexDirection="row"
          gridGap="S200"
        >
          <ButtonWithLoader
            data-testid="create-multi-campaign-btn"
            fontSize="T500"
            isLoading={loading}
            isDisabled={readonly}
            isPrimary={true}
            onClickHandler={onCreateJourneyClick}
          >
            <FormattedMessage
              id="app.groupCampaign.createMultiCampaign"
              defaultMessage="Create a group campaign"
            />
          </ButtonWithLoader>
          <Spacer width="10px" />
          <Button fontSize="T500" variant="tertiary" onClick={() => history.push(HOME_PAGE)}>
            <FormattedMessage id="app.groupCampaign.cancel" defaultMessage="Cancel" />
          </Button>
        </Col>
        <Spacer height="20px" />
      </Col>
    </>
  );
};

export default JourneyContainer;
